package org.example.project.registrierung

import kotlinx.browser.window
import kotlinx.coroutines.await
import kotlinx.serialization.builtins.ListSerializer
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import kotlinx.serialization.json.put
import org.example.project.manager.LumaticaApiManager
import org.example.project.manager.LumaticaApiSettings
import org.example.project.utils.All
import org.w3c.fetch.Headers
import org.w3c.fetch.RequestInit

object RegistrierungApi {
    private val json = Json {
        ignoreUnknownKeys = true
        encodeDefaults = false
    }

    suspend fun login(email: String, password: String): All? =
        decodeOne(postPasswordJson("registrierung/login-json", email, "", password))

    suspend fun loadCompanies(): List<All> =
        decodeList(LumaticaApiManager.call("registrierung/companies"))

    suspend fun emailExists(email: String): Boolean {
        val raw = LumaticaApiManager.call("registrierung/email-exists/${apiPart(email.lowercase())}")
        return raw.contains("\"exists\":true")
    }

    suspend fun changePassword(userId: String, firmenId: String, password: String): All? =
        decodeOne(postPasswordJson("registrierung/password-change-json", userId, firmenId, password))

    suspend fun saveCompany(firma: All): All? =
        decodeOne(postCompanyJson("registrierung/save-company-json", firma))

    suspend fun registerDriver(fahrer: All, password: String, signatur: String): All? {
        return registerDriverDetailed(fahrer, password, signatur).user
    }

    suspend fun previewDriverSnapKey(snapKey: String): All? =
        decodeOne(
            LumaticaApiManager.call(
                listOf("registrierung", "preview-driver-snapkey", snapKey).joinToString("/") { apiPart(it) }
            )
        )

    suspend fun registerDriverDetailed(fahrer: All, password: String, signatur: String): DriverRegistrationResponse {
        val response = window.fetch(
            "${LumaticaApiSettings.getBaseUrl()}/api/registrierung/register-driver-json",
            RequestInit(
                method = "POST",
                headers = Headers().apply { append("Content-Type", "application/json") },
                body = json.encodeToString(
                    buildJsonObject {
                        put("password", password)
                        put("signatur", signatur)
                        put("fahrer", json.encodeToJsonElement(All.AllNames.serializer(), fahrer))
                    }
                )
            )
        )
            .await()
        if (!response.ok) return DriverRegistrationResponse()
        return decodeDriverRegistrationResponse(json, response.text().await())
    }

    private fun decodeOne(raw: String): All? =
        runCatching { json.decodeFromString(All.AllNames.serializer(), raw) }.getOrNull()

    private fun decodeList(raw: String): List<All> =
        runCatching { json.decodeFromString(ListSerializer(All.AllNames.serializer()), raw) }.getOrDefault(emptyList())

    private suspend fun postCompanyJson(endpoint: String, firma: All): String {
        val response = window.fetch(
            "${LumaticaApiSettings.getBaseUrl()}/api/$endpoint",
            RequestInit(
                method = "POST",
                headers = Headers().apply { append("Content-Type", "application/json") },
                body = json.encodeToString(All.AllNames.serializer(), firma)
            )
        ).await()
        return response.text().await()
    }

    private suspend fun postPasswordJson(endpoint: String, first: String, firmenId: String, password: String): String {
        val response = window.fetch(
            "${LumaticaApiSettings.getBaseUrl()}/api/$endpoint",
            RequestInit(
                method = "POST",
                headers = Headers().apply { append("Content-Type", "application/json") },
                body = json.encodeToString(
                    buildJsonObject {
                        if (endpoint.endsWith("login-json")) {
                            put("email", first.trim().lowercase())
                        } else {
                            put("userId", first)
                            put("firmenId", firmenId)
                        }
                        put("password", password)
                    }
                )
            )
        ).await()
        return response.text().await()
    }

    private fun apiPart(value: String): String =
        js("encodeURIComponent")(if (value.isBlank()) "~" else value) as String
}

typealias DriverRegistrationResponse = All

internal fun decodeDriverRegistrationResponse(json: Json, raw: String): DriverRegistrationResponse {
    if (raw.isBlank() || raw == "null") return DriverRegistrationResponse()
    return runCatching {
        val root = json.parseToJsonElement(raw).jsonObject
        DriverRegistrationResponse(
            ok = root["ok"]?.jsonPrimitive?.contentOrNull?.toBoolean() == true,
            error = root["error"]?.jsonPrimitive?.contentOrNull.orEmpty(),
            firmenId = root["firmenId"]?.jsonPrimitive?.contentOrNull.orEmpty(),
            user = root["user"]?.let {
                runCatching { json.decodeFromString(All.AllNames.serializer(), it.toString()) }.getOrNull()
            }
        )
    }.getOrDefault(DriverRegistrationResponse())
}
